/**
 * SA-454: the map's words, beside its components — the repo's convention for
 * text a component exports (`processEventText.ts`, `pricingText.ts`), and what
 * keeps `MapCard` and `MapList` exporting components only, so React Fast
 * Refresh works on them.
 */
import type { ProcessStepVM } from '@/utils/workflowOverview'
import { edgeWords } from './layout'

/** The executor kinds whose level is a fact worth printing (a wait, an automation, a conversation have none). */
const LEVELLED = new Set<ProcessStepVM['executorType']>(['person', 'ai'])

/** A card's accessible name (spec §4.6; SA-460 adds the level: "…, Ground Operations, with AI, assisted, done";
 * SA-1037 adds ", skipped" for a step an exit jumped over). */
export function accessibleName(step: ProcessStepVM, current: boolean, visited: boolean, skipped = false): string {
  const level = LEVELLED.has(step.executorType) && step.level ? `, ${step.level}` : ''
  return `Step ${step.number} ${step.title}, ${step.who}${level}${current ? ', current' : visited ? ', done' : skipped ? ', skipped' : ''}`
}

/** The card's due line (spec §4.1): the wireframe prints the absence. */
export function dueLine(step: Pick<ProcessStepVM, 'due'>): string {
  return step.due ? `due ${step.due}` : 'no deadline'
}

/** `MANUAL` / `ASSISTED` / `AUTOMATIC` on a person or ai step; null elsewhere. */
export function levelMark(step: Pick<ProcessStepVM, 'executorType' | 'level'>): string | null {
  return LEVELLED.has(step.executorType) && step.level ? step.level.toUpperCase() : null
}

/** The "next:" line a card carries in list mode (spec §4.2), where there are no edges. */
export function exitsLine(step: ProcessStepVM): string {
  return step.exits.map((e) => (e.targetKind === 'step' && e.target !== step.id ? edgeWords(e.name) : `${edgeWords(e.name)} → ${e.targetLabel}`)).join(' / ')
}

/** The list row's second line (SA-459, spec §4.4): `seat · level · due 4 hours · next: …`. */
export function rowMeta(step: ProcessStepVM): string {
  return `${[step.who, step.level, dueLine(step)].filter(Boolean).join(' · ')} · next: ${exitsLine(step)}`
}
